import React from 'react';
import { X, AlertCircle, HelpCircle } from 'lucide-react';
export function PainSection() {
  return <section className="py-16 md:py-24 bg-white px-5 border-b border-gray-100" data-id="element-164">
      <div className="max-w-4xl mx-auto relative z-10" data-id="element-165">
        {/* Section 1 */}
        <div className="mb-24" data-id="element-166">
          <div className="text-center mb-16" data-id="element-167">
            <span className="text-rose-600 font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-168">
              Reflexión
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-8 leading-tight tracking-tight" data-id="element-169">
              ¿Sentís que tu cuerpo cambió y ya no responde como antes?
            </h2>
            <p className="text-2xl text-gray-600 font-serif italic max-w-3xl mx-auto" data-id="element-170">
              La menopausia trae cambios que pueden ser abrumadores si no sabés
              cómo gestionarlos.
            </p>
          </div>

          <div className="bg-white p-10 md:p-14 rounded-[2rem] shadow-xl border border-rose-100 mb-16 relative overflow-hidden group hover:shadow-2xl transition-shadow duration-500" data-id="element-171">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-rose-300 to-rose-500" data-id="element-172"></div>
            <div className="absolute -right-20 -top-20 w-64 h-64 bg-rose-50 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition-opacity" data-id="element-173"></div>

            <div className="relative z-10" data-id="element-174">
              <p className="text-gray-600 mb-6 text-xl leading-relaxed" data-id="element-175">
                No es que estés haciendo las cosas mal. No es falta de voluntad.
              </p>
              <p className="text-gray-900 mb-6 font-bold text-2xl" data-id="element-176">
                Es que tus hormonas cambiaron las reglas del juego.
              </p>
              <p className="text-gray-600 text-xl leading-relaxed" data-id="element-177">
                Probás comer "sano", hacés dietas que antes funcionaban, pero
                ahora la balanza no se mueve, la energía no vuelve y la
                inflamación es constante.
              </p>
            </div>
          </div>

          <p className="text-lg font-bold text-gray-800 mb-10 text-center uppercase tracking-wide" data-id="element-178">
            Síntomas comunes en esta etapa:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20" data-id="element-179">
            {['Sofocos y cambios de humor repentinos', 'Inflamación y retención de líquidos', 'Falta de energía y concentración (niebla mental)', 'Metabolismo cada vez más lento', 'Dificultad para dormir bien y descansar'].map((item, index) => <div key={index} className="flex items-center gap-5 bg-white p-6 rounded-2xl shadow-sm border-l-4 border-l-rose-200 border-y border-r border-gray-100 hover:border-l-rose-500 hover:shadow-md transition-all group" data-id="element-180">
                <div className="bg-rose-50 p-2.5 rounded-full shrink-0 group-hover:bg-rose-100 transition-colors" data-id="element-181">
                  <X className="w-5 h-5 text-rose-500" strokeWidth={2.5} data-id="element-182" />
                </div>
                <span className="text-gray-700 font-medium text-lg group-hover:text-gray-900 transition-colors" data-id="element-183">
                  {item}
                </span>
              </div>)}
          </div>
        </div>

        {/* Section 2 */}
        <div className="bg-white p-10 md:p-16 rounded-[2.5rem] shadow-2xl border border-gray-100 relative overflow-hidden" data-id="element-184">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rose-50 rounded-full blur-[80px] opacity-60 -mr-20 -mt-20 pointer-events-none" data-id="element-185"></div>

          <div className="relative z-10" data-id="element-186">
            <div className="flex justify-center mb-10" data-id="element-187">
              <div className="bg-white/80 backdrop-blur-sm border border-rose-100 text-rose-800 px-6 py-2.5 rounded-full font-bold text-sm uppercase tracking-wide flex items-center gap-2 shadow-sm" data-id="element-188">
                <AlertCircle className="w-4 h-4" data-id="element-189" />
                El problema real
              </div>
            </div>

            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-10 text-center leading-tight" data-id="element-190">
              El problema que nadie te dice sobre la menopausia
            </h2>

            <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto leading-relaxed" data-id="element-191">
              Si esto te pasa, no es tu culpa.{' '}
              <span className="bg-amber-50 text-amber-900 px-3 py-1 rounded-lg mx-1 font-bold border border-amber-100" data-id="element-192">
                Es falta de información específica para tu etapa.
              </span>
            </p>

            <ul className="space-y-6 mb-14 max-w-2xl mx-auto" data-id="element-193">
              {['No sabés qué comer para equilibrar tus hormonas', 'Las dietas genéricas no consideran tu etapa actual', 'Te sentís hinchada aunque comas sano', 'Nadie te explica cómo funciona tu cuerpo ahora', 'Arrancás y abandonás porque no ves resultados'].map((item, index) => <li key={index} className="flex items-start gap-5 p-5 rounded-2xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100 group" data-id="element-194">
                  <div className="mt-1 bg-rose-50 rounded-full p-2 group-hover:bg-rose-100 transition-colors" data-id="element-195">
                    <HelpCircle className="w-5 h-5 text-rose-600" data-id="element-196" />
                  </div>
                  <span className="text-gray-700 text-lg font-medium group-hover:text-gray-900 transition-colors" data-id="element-197">
                    {item}
                  </span>
                </li>)}
            </ul>

            <div className="bg-gray-50 p-10 rounded-3xl border border-gray-100 mb-14 text-center relative overflow-hidden" data-id="element-198">
              <div className="absolute inset-0 bg-noise opacity-50" data-id="element-199"></div>
              <p className="text-2xl text-gray-800 font-serif italic relative z-10" data-id="element-200">
                Lo peor es esa sensación constante de:
                <br data-id="element-201" />
                <br data-id="element-202" />
                <span className="text-3xl font-medium text-gray-900" data-id="element-203">
                  "Sé que podría sentirme mejor… pero no sé por dónde empezar."
                </span>
              </p>
            </div>

            <div className="text-center" data-id="element-204">
              <a href="#compra" className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white text-lg md:text-xl font-bold py-5 px-12 rounded-full shadow-xl hover:shadow-emerald-500/30 transition-all transform hover:-translate-y-1 border border-emerald-500/20" data-id="element-205">
                QUIERO SENTIRME MEJOR HOY
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}